import type { FilledMessage, Parameters, Template } from './types';

const emptyMessage = (): FilledMessage => ({
  HEADER: '',
  BODY: '',
  FOOTER: '',
  BUTTONS: [
    { text: '', url: '' },
    { text: '', url: '' },
    { text: '', url: '' },
  ],
});

/**
 * Fill a template's components with the entered parameter values to build the live preview
 * (header/body/footer text + buttons). When the template has no parameters, the raw template
 * text is used as-is.
 */
export default function buildTemplateMessage(template: Template | null, parameters: Parameters): FilledMessage {
  const message = emptyMessage();
  if (!template) return message;

  const hasParams =
    parameters.HEADER.length > 0 ||
    parameters.BODY.length > 0 ||
    parameters.FOOTER.length > 0 ||
    parameters.BUTTON.length > 0;

  template.components.forEach((component) => {
    if (component.text && component.type !== 'BUTTONS') {
      if (!hasParams) {
        message[component.type] = component.text;
        return;
      }
      const count = (component.text.match(/{{[1-9]}}/g) || []).length;
      let newText = component.text;
      for (let i = 0; i < count; i += 1) {
        const re = new RegExp(`{{[${i + 1}]}}`, 'g');
        newText = newText.replace(re, parameters[component.type][i]?.text || `<parámetro ${i + 1}>`);
      }
      if (component.example?.body_text_named_params) {
        parameters[component.type]?.forEach((parameter) => {
          if (parameter.parameter_name) {
            newText = newText.replace(
              `{{${parameter.parameter_name}}}`,
              parameter.text || `<parámetro ${parameter.parameter_name}>`
            );
          }
        });
      }
      message[component.type] = newText;
    } else if (component.buttons) {
      if (!hasParams) {
        component.buttons.forEach((button, index) => {
          message.BUTTONS[index].text = button.text;
          message.BUTTONS[index].url = button.url || '';
        });
        return;
      }
      // There can be up to 2 buttons but only one carries a URL.
      let buttonWithUrl: number | undefined;
      if (component.buttons[0].url) {
        buttonWithUrl = 0;
      } else if (component.buttons.length === 2 && component.buttons[1].url) {
        buttonWithUrl = 1;
      }
      if (buttonWithUrl !== undefined) {
        let url = component.buttons[buttonWithUrl].url || '';
        const count = (url.match(/{{[1-9]}}/g) || []).length;
        for (let i = 0; i < count; i += 1) {
          const re = new RegExp(`{{[${i + 1}]}}`, 'g');
          url = url.replace(re, parameters.BUTTON[i]?.text || '');
        }
        message.BUTTONS[buttonWithUrl].url = url;
      }
      component.buttons.forEach((button, index) => {
        message.BUTTONS[index].text = button.text;
      });
    }
  });
  return message;
}
